import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useServiceEditAccess } from "@/hooks/useServiceEditAccess";

interface CalendarSettingsTabProps {
  serviceId: string | undefined;
}

interface CalendarSettingsData {
  monday_start?: string | null;
  monday_end?: string | null;
  tuesday_start?: string | null;
  tuesday_end?: string | null;
  wednesday_start?: string | null;
  wednesday_end?: string | null;
  thursday_start?: string | null;
  thursday_end?: string | null;
  friday_start?: string | null;
  friday_end?: string | null;
  saturday_start?: string | null;
  saturday_end?: string | null;
  sunday_start?: string | null;
  sunday_end?: string | null;
  slot_size: string;
  travel_time_enabled: boolean;
  travel_duration: string;
}

const timeOptions = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minute}`;
});

const CalendarSettingsTab: React.FC<CalendarSettingsTabProps> = ({ serviceId }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { canAccessCalendarSettings, isLoading: permissionLoading } = useServiceEditAccess(serviceId);
  const [formData, setFormData] = useState<CalendarSettingsData>({
    slot_size: "60",
    travel_time_enabled: false,
    travel_duration: "30"
  });

  useEffect(() => {
    const fetchServiceHours = async () => {
      if (serviceId) {
        try {
          setLoading(true);
          
          const { data: basicData, error: basicError } = await supabase
            .from('services')
            .select(`
              monday_start, monday_end,
              tuesday_start, tuesday_end,
              wednesday_start, wednesday_end,
              thursday_start, thursday_end,
              friday_start, friday_end,
              saturday_start, saturday_end,
              sunday_start, sunday_end,
              slot_size,
              travel_time_enabled,
              travel_duration
            `)
            .eq('id', parseInt(serviceId))
            .single();
          
          if (basicError) {
            console.error("Error fetching service hours:", basicError);
            toast({
              title: "Error",
              description: "Failed to load service hours",
              variant: "destructive",
            });
            return;
          }
          
          if (basicData) {
            setFormData({
              ...basicData
            });
          } else {
            setFormData({
              monday_start: "",
              monday_end: "",
              tuesday_start: "",
              tuesday_end: "",
              wednesday_start: "",
              wednesday_end: "",
              friday_start: "",
              friday_end: "",
              saturday_start: "",
              saturday_end: "",
              sunday_start: "",
              sunday_end: "",
              slot_size: "60",
              travel_time_enabled: false,
              travel_duration: "30"
            });
          }
        } catch (error) {
          console.error('Error fetching service hours:', error);
          toast({
            title: "Error",
            description: "Failed to load service hours",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchServiceHours();
  }, [serviceId, toast]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!canAccessCalendarSettings) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to update calendar settings",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const updateData: Record<string, any> = {
        monday_start: formData.monday_start === "none" ? null : formData.monday_start,
        monday_end: formData.monday_end === "none" ? null : formData.monday_end,
        tuesday_start: formData.tuesday_start === "none" ? null : formData.tuesday_start,
        tuesday_end: formData.tuesday_end === "none" ? null : formData.tuesday_end,
        wednesday_start: formData.wednesday_start === "none" ? null : formData.wednesday_start,
        wednesday_end: formData.wednesday_end === "none" ? null : formData.wednesday_end,
        thursday_start: formData.thursday_start === "none" ? null : formData.thursday_start,
        thursday_end: formData.thursday_end === "none" ? null : formData.thursday_end,
        friday_start: formData.friday_start === "none" ? null : formData.friday_start,
        friday_end: formData.friday_end === "none" ? null : formData.friday_end,
        saturday_start: formData.saturday_start === "none" ? null : formData.saturday_start,
        saturday_end: formData.saturday_end === "none" ? null : formData.saturday_end,
        sunday_start: formData.sunday_start === "none" ? null : formData.sunday_start,
        sunday_end: formData.sunday_end === "none" ? null : formData.sunday_end,
        slot_size: formData.slot_size,
        travel_time_enabled: formData.travel_time_enabled,
        travel_duration: formData.travel_duration
      };
      
      const { error } = await supabase
        .from('services')
        .update(updateData)
        .eq('id', serviceId ? parseInt(serviceId) : 0);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Calendar settings updated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      toast({
        title: "Error",
        description: "Failed to save calendar settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (permissionLoading) {
    return <div>Loading...</div>;
  }

  if (!canAccessCalendarSettings) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Restricted</AlertTitle>
        <AlertDescription>
          You don't have permission to view calendar settings.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Working Hours Card */}
      <Card>
        <CardHeader>
          <CardTitle>Working Hours</CardTitle>
          <CardDescription>Set your available days and operating hours for this service</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-24 py-2">Day</TableHead>
                <TableHead className="w-32 py-2">Start</TableHead>
                <TableHead className="w-32 py-2">End</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <TableRow key={day} className="hover:bg-transparent">
                  <TableCell className="font-medium capitalize py-1.5">
                    {day}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Select
                      value={formData[`${day}_start` as keyof CalendarSettingsData]?.toString() || "none"}
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        [`${day}_start`]: value === "none" ? null : value 
                      }))}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not available</SelectItem>
                        {timeOptions.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Select
                      value={formData[`${day}_end` as keyof CalendarSettingsData]?.toString() || "none"}
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        [`${day}_end`]: value === "none" ? null : value 
                      }))}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not available</SelectItem>
                        {timeOptions.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Calendar Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Calendar Settings
          </CardTitle>
          <CardDescription>Configure display settings, appointment slots, and travel time</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="w-full max-w-xs">
            <label className="block text-sm font-medium mb-1">Default Slot Size</label>
            <Select
              value={formData.slot_size}
              onValueChange={(value) => setFormData(prev => ({ ...prev, slot_size: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 Minutes</SelectItem>
                <SelectItem value="30">30 Minutes</SelectItem>
                <SelectItem value="60">1 Hour</SelectItem>
                <SelectItem value="90">1.5 Hours</SelectItem>
                <SelectItem value="120">2 Hours</SelectItem>
                <SelectItem value="150">2.5 Hours</SelectItem>
                <SelectItem value="180">3 Hours</SelectItem>
                <SelectItem value="240">4 Hours</SelectItem>
                <SelectItem value="allDay">All Day</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Default duration when creating new bookings. Calendar slots will match this size.
            </p>
          </div>

          <div className="border p-4 rounded-md space-y-4">
            <h3 className="font-medium">Travel Time Settings</h3>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="travel-time-enabled"
                checked={formData.travel_time_enabled}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, travel_time_enabled: checked }))
                }
              />
              <Label htmlFor="travel-time-enabled">Enable travel time between appointments</Label>
            </div>
            
            <div className="w-full max-w-xs">
              <label className="block text-sm font-medium mb-1">Travel Duration</label>
              <Select
                value={formData.travel_duration}
                onValueChange={(value) => setFormData(prev => ({ ...prev, travel_duration: value }))}
                disabled={!formData.travel_time_enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Minutes</SelectItem>
                  <SelectItem value="60">60 Minutes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Time needed to travel between appointments
              </p>
            </div>
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Calendar Settings"}
          </Button>
        </CardContent>
      </Card>
    </form>
  );
};

export default CalendarSettingsTab;
